import { WORLD_SECTIONS } from "./visitorContent.js";

/**
 * Recursively freeze application data so accidental runtime mutations fail
 * early during development.
 *
 * @template T
 * @param {T} value
 * @returns {Readonly<T>}
 */
function deepFreeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }

  return value;
}

export const PROFILE = deepFreeze({
  name: "Prem Jethwa",
  title: "Senior Full-Stack Engineer · Web, Mobile & Applied AI",
  location: "Mumbai, India",
  region: "ap-south-1",
  email: "heypremm@gmail.com",
  github: "https://github.com/Prem-Jethwa",
  linkedin: "https://www.linkedin.com/in/premjethwa",
  resumePath: "resume/Prem_Jethwa_Resume.pdf",
  experience: 7,
  experienceStatus: "Experience timeline supplied in Prem’s résumé.",

  positioning: "I build the apps people use—and the systems behind them.",

  introduction:
    "I develop websites, mobile apps, business tools, and AI-assisted features that connect the customer experience with the systems behind it.",

  operatingPrinciple:
    "Useful before impressive. Recoverable before autonomous.",
});

export const AGENT_PIPELINE = deepFreeze({
  title: "A live run, not a diagram.",

  description:
    "Deterministic filters before any model call, batch scoring to amortise prompts, and a human gate before anything irreversible.",

  stages: [
    {
      id: "source",
      label: "Source",
      type: "deterministic",
    },
    {
      id: "filter",
      label: "Hard filter",
      type: "deterministic",
    },
    {
      id: "score",
      label: "Batch score",
      type: "model",
    },
    {
      id: "draft",
      label: "Draft",
      type: "model",
    },
    {
      id: "review",
      label: "Human review",
      type: "human-gate",
    },
    {
      id: "send",
      label: "Release",
      type: "irreversible",
    },
  ],

  metrics: [
    {
      value: "Required",
      label: "Human review before sending",
    },
    {
      value: "Rules first",
      label: "Hard filters before model reasoning",
    },
    {
      value: "Queued",
      label: "Background task processing",
    },
  ],
});

export const PRINCIPLES = deepFreeze([
  {
    id: "blocked-send",
    number: "P-01",
    title: "A blocked send beats a broken one",

    description:
      "My job-hunt agent once drafted a confidently wrong visa answer. It never shipped—because nothing irreversible happens without a human. That rule is architecture, not policy.",

    worldLocation: "human-gate",
  },

  {
    id: "filter-first",
    number: "P-02",
    title: "Filter before you infer",

    description:
      "Most rejected work should cost zero model calls. Deterministic hard filters, dedupe hashes, and batch prompts come before any token is spent.",

    worldLocation: "agent-workshop",
  },

  {
    id: "interpretable",
    number: "P-03",
    title: "Interpretable beats clever at low N",

    description:
      "With dozens of outcomes rather than millions, a Beta-per-bucket model stays honest where a dense model can overfit. I can read exactly what the system has learned.",

    worldLocation: "agent-workshop",
  },

  {
    id: "traceable",
    number: "P-04",
    title: "Traceable or it didn't happen",

    description:
      "Request-scoped logs, state-preserving releases, and WAL checkpoints before upgrades turn a black box into a system that can be investigated and recovered.",

    worldLocation: "incident-room",
  },
]);

export const CAPABILITIES = deepFreeze({
  agents: [
    "Multi-agent orchestration",
    "Human-in-the-loop design",
    "Claude CLI and API",
    "Prompt caching and batch economics",
    "Thompson sampling",
    "Interpretable feedback loops",
  ],

  backend: [
    "Node.js",
    "TypeScript",
    "Event-driven systems",
    "BullMQ",
    "Redis",
    "MongoDB",
    "SQLite WAL",
    "Restart-safe queue semantics",
  ],

  frontend: [
    "React",
    "React Native",
    "Next.js",
    "Vite",
    "Konva",
    "Zustand",
    "Redux Saga",
    "Tailwind CSS",
  ],

  infrastructure: [
    "Docker",
    "Railway",
    "Cloudflare R2",
    "Self-hosted NAS automation",
    "Playwright",
    "ffmpeg",
    "Turborepo",
  ],

  judgement: [
    "Knows when an agent should stop and ask a human",
    "Designs for recovery instead of hiding failure",
    "Uses deterministic code where probabilistic reasoning adds no value",
  ],
});

export const PROJECTS = deepFreeze([
  {
    id: "reel-creation",
    title: "ReelChain",
    category: "Video creation · Creator tools",
    worldZone: "products",
    summary:
      "Plan, script, storyboard and assemble short-form video in one guided workspace.",
    proof:
      "I built the creation workflow from research and hooks through visual planning, reel assembly and browser-based export.",
    stack: [
      "React",
      "TypeScript",
      "Zustand",
      "Node.js",
      "Playwright",
      "ffmpeg",
    ],
    metrics: [
      {
        value: "Guided",
        label: "Recording to reel",
      },
      {
        value: "Prompt-based",
        label: "Supporting visuals",
      },
    ],
    steps: [
      "Research a topic and choose an angle",
      "Shape the brief, hook and script",
      "Plan scenes and add supporting visuals",
      "Assemble, preview and export the reel",
    ],
    decision:
      "Let creators generate images through tools they already use. This can avoid an additional image-generation API charge; external subscriptions and limits still apply.",
    media: [
      {
        type: "image",
        src: "images/projects/reel-creation.webp",
        title: "Research workspace",
        caption: "Actual ReelChain interface with a sample portfolio brief.",
      },
      {
        type: "image",
        src: "images/projects/reel-creation-brief.webp",
        title: "Set the brief",
        caption: "Duration, platform and audience controls in the running app.",
      },
      {
        type: "video",
        src: "videos/projects/reelchain-created-reel.mp4",
        poster: "images/projects/reelchain-created-reel.webp",
        title: "Reel created with ReelChain",
        caption:
          "A finished reel created by Prem with ReelChain, featuring his recording, supporting visuals and captions.",
        portrait: true,
      },
    ],
  },
  {
    id: "connect-acf-acp",
    title: "ContentStudio",
    category: "Content automation · Generation to publishing",
    worldZone: "products",
    summary:
      "An end-to-end content workflow that generates reels, carousels and stories, then schedules and posts them across connected social accounts.",
    proof:
      "I connected the content factory and social publishing services into one workflow, with brand-specific generation, background processing, review controls and posting history.",
    stack: ["Node.js", "TypeScript", "BullMQ", "Redis", "Playwright", "ffmpeg"],
    metrics: [
      {
        value: "Multi-format",
        label: "Reels, carousels and stories",
      },
      {
        value: "End-to-end",
        label: "Generation through posting",
      },
    ],
    steps: [
      "Configure a brand, content series and schedule",
      "Generate copy, visuals and rendered content",
      "Review drafts or use configured automatic approval",
      "Schedule, publish and track delivery across accounts",
    ],
    decision:
      "Keep brand identity and account routing explicit, with configurable autonomy and a durable publishing queue.",
    media: [
      {
        type: "image",
        src: "images/projects/connect-acf-acp.webp",
        title: "Content review & publishing",
        caption:
          "Actual local dashboard showing generated content and saved posting history.",
      },
      {
        type: "image",
        src: "images/projects/connect-acf-acp-carousels.webp",
        title: "Carousel review queue",
        caption:
          "Generated carousel drafts in the local review dashboard, filtered by content format.",
      },
      {
        type: "carousel",
        src: "images/projects/connect-carousel-1.webp",
        title: "Generated carousel · 5 cards",
        caption:
          "One complete generated carousel explaining gold jewellery making charges. Swipe through the cards or open a card to enlarge it.",
        items: [
          {
            type: "image",
            src: "images/projects/connect-carousel-1.webp",
            title: "Carousel · 1 of 5",
            caption:
              "Generated carousel: making charges explained. Slide 1 of 5 — why the final bill exceeds the gold price.",
          },
          {
            type: "image",
            src: "images/projects/connect-carousel-2.webp",
            title: "Carousel · 2 of 5",
            caption:
              "Generated carousel: making charges explained. Slide 2 of 5.",
          },
          {
            type: "image",
            src: "images/projects/connect-carousel-3.webp",
            title: "Carousel · 3 of 5",
            caption:
              "Generated carousel: making charges explained. Slide 3 of 5.",
          },
          {
            type: "image",
            src: "images/projects/connect-carousel-4.webp",
            title: "Carousel · 4 of 5",
            caption:
              "Generated carousel: making charges explained. Slide 4 of 5.",
          },
          {
            type: "image",
            src: "images/projects/connect-carousel-5.webp",
            title: "Carousel · 5 of 5",
            caption:
              "Generated carousel: making charges explained. Slide 5 of 5 — the closing card.",
          },
        ],
      },
      {
        type: "video",
        src: "videos/projects/connect-created-reel.mp4",
        poster: "images/projects/connect-created-reel.webp",
        title: "Generated reel",
        caption:
          "A finished ContentStudio reel selected by Prem, with animated visuals, narration and on-screen captions.",
        portrait: true,
      },
      {
        type: "video",
        src: "videos/projects/connect-story.mp4",
        poster: "images/projects/connect-story.webp",
        title: "Generated story",
        caption:
          "A rendered story from the content factory’s brand preview output. Silent playback.",
        portrait: true,
      },
    ],
  },
  {
    id: "billflow-tds",
    title: "BillFlow",
    category: "Business software · Billing & accounting",
    worldZone: "products",
    summary:
      "A billing workspace for Indian businesses: GST invoices, customer ledgers, payments, reports and TDS workflows.",
    proof:
      "I built the billing interface and backend workflows for business accounts, invoices, GST calculations, payment tracking and TDS deductions, challans and filing preparation.",
    stack: ["React", "Redux Toolkit", "Node.js", "Express", "MongoDB"],
    metrics: [
      {
        value: "GST",
        label: "Invoice and tax workflows",
      },
      {
        value: "TDS",
        label: "Deductions and filing preparation",
      },
    ],
    steps: [
      "Set up a business and add customers or suppliers",
      "Create invoices with line items and GST calculations",
      "Track payments, balances and reports",
      "Manage TDS deductions, challans and quarterly filing preparation",
    ],
    decision:
      "Keep invoice snapshots, business boundaries and audit history explicit so records stay traceable.",
    media: [
      {
        type: "image",
        src: "images/projects/billflow-tds.webp",
        title: "Billing dashboard",
        caption:
          "Actual BillFlow interface in a local preview. All businesses, invoices and amounts are sample data.",
      },
      {
        type: "image",
        src: "images/projects/billflow-tds-filing.webp",
        title: "TDS workspace",
        caption:
          "Quarterly TDS view with sample data; no filing was submitted.",
      },
    ],
  },
  {
    id: "orchestrator",
    title: "Orchestrator",
    category: "Developer tools · AI automation",
    worldZone: "tower",
    summary:
      "Turn product requirements into phased development with AI coding sessions, saved progress, checks, audits, and repair cycles.",
    proof:
      "I designed the coordination layer around Claude CLI: planning and development roles, durable SQLite queues, recovery, and a dashboard for operator control.",
    stack: ["Node.js", "Claude CLI", "SQLite", "Git worktrees", "Vue 3", "MCP"],
    metrics: [
      {
        value: "Phased",
        label: "Plan, build, check, audit",
      },
      {
        value: "Resumable",
        label: "Saved work and queues",
      },
    ],
    steps: [
      "Describe the product requirements",
      "Review the phased plan",
      "Run development and configured checks",
      "Inspect audits, fixes, and results",
    ],
    decision:
      "Persist work independently of AI sessions so interruptions and usage limits can be handled explicitly. Some checks report findings; configured gates can block completion.",
    media: [
      {
        type: "image",
        src: "images/projects/orchestrator.webp",
        title: "Saved development phases",
        caption:
          "Actual saved phase history. Captured in a local read-only session with execution disabled.",
      },
      {
        type: "image",
        src: "images/projects/orchestrator-overview.webp",
        title: "Project overview & run totals",
        caption:
          "Saved project progress and usage totals in the local dashboard. Execution was disabled for this capture.",
      },
    ],
  },
  {
    id: "agent-marketplace",
    title: "AgentDukaan",
    category: "Platform · Reusable AI tools",
    worldZone: "agent",
    summary:
      "A marketplace to discover hosted AI agents, buy source code, or find help setting up an agent for your business.",
    proof:
      "I built the marketplace frontend and API around agent discovery, creator listings, subscriptions and hosted agent access.",
    stack: ["Next.js", "React", "TypeScript", "Express", "MongoDB"],
    metrics: [
      {
        value: "Discover",
        label: "Creator-published agents",
      },
      {
        value: "Reuse",
        label: "Supported workflows",
      },
    ],
    steps: [
      "Browse agents by category and use case",
      "Compare capabilities and pricing",
      "Subscribe to a hosted agent, buy source, or request setup",
    ],
    decision:
      "Make compatibility and pricing clear before purchase so buyers understand where an agent can be used.",
    url: "https://agentdukaan.in",
    media: [
      {
        type: "image",
        src: "images/projects/agent-marketplace.webp",
        title: "Agent marketplace",
        caption:
          "Running local marketplace showing agent listings, categories and pricing.",
      },
      {
        type: "image",
        src: "images/projects/agent-marketplace-detail.webp",
        title: "Agent listing & product demo",
        caption:
          "Local product listing showing the built-in demo, plans and capabilities. Figures shown are listing content.",
      },
    ],
  },
]);

export const ZONES = deepFreeze([
  {
    id: "studio",
    number: "00",
    title: WORLD_SECTIONS.studio.label,
    shortTitle: WORLD_SECTIONS.studio.label,
    worldTitle: WORLD_SECTIONS.studio.creativeTitle,
    headline: WORLD_SECTIONS.studio.label,
    description: WORLD_SECTIONS.studio.description,
    projectIds: [],
  },
  {
    id: "agent",
    number: "01",
    title: WORLD_SECTIONS.agent.label,
    shortTitle: WORLD_SECTIONS.agent.label,
    worldTitle: WORLD_SECTIONS.agent.creativeTitle,
    headline: WORLD_SECTIONS.agent.label,
    description: WORLD_SECTIONS.agent.description,
    projectIds: ["agent-marketplace"],
  },
  {
    id: "tower",
    number: "02",
    title: WORLD_SECTIONS.tower.label,
    shortTitle: WORLD_SECTIONS.tower.label,
    worldTitle: WORLD_SECTIONS.tower.creativeTitle,
    headline: WORLD_SECTIONS.tower.label,
    description: WORLD_SECTIONS.tower.description,
    projectIds: ["orchestrator"],
  },
  {
    id: "products",
    number: "03",
    title: WORLD_SECTIONS.products.label,
    shortTitle: WORLD_SECTIONS.products.label,
    worldTitle: WORLD_SECTIONS.products.creativeTitle,
    headline: WORLD_SECTIONS.products.label,
    description: WORLD_SECTIONS.products.description,
    projectIds: ["reel-creation", "connect-acf-acp", "billflow-tds"],
  },
  {
    id: "incident",
    number: "04",
    title: WORLD_SECTIONS.incident.label,
    shortTitle: WORLD_SECTIONS.incident.label,
    worldTitle: WORLD_SECTIONS.incident.creativeTitle,
    headline: WORLD_SECTIONS.incident.label,
    description: WORLD_SECTIONS.incident.description,
    projectIds: [],
  },
  {
    id: "contact",
    number: "05",
    title: WORLD_SECTIONS.contact.label,
    shortTitle: WORLD_SECTIONS.contact.label,
    worldTitle: WORLD_SECTIONS.contact.creativeTitle,
    headline: WORLD_SECTIONS.contact.label,
    description: WORLD_SECTIONS.contact.description,
    projectIds: [],
  },
]);

export const SITE = deepFreeze({
  title: `${PROFILE.name} — ${PROFILE.title}`,

  description:
    "A human-first interactive 3D portfolio focused on dependable products, recoverable systems, responsible automation, and engineering judgment.",

  themeColor: "#171a16",
  projectCount: PROJECTS.length,
  zoneCount: ZONES.length,
});

/**
 * @param {string} projectId
 */
export function getProjectById(projectId) {
  return PROJECTS.find((project) => project.id === projectId);
}

/**
 * @param {string} zoneId
 */
export function getZoneById(zoneId) {
  return ZONES.find((zone) => zone.id === zoneId);
}

/**
 * @param {string} zoneId
 */
export function getProjectsForZone(zoneId) {
  const zone = getZoneById(zoneId);

  if (!zone) {
    return [];
  }

  return zone.projectIds.map(getProjectById).filter(Boolean);
}
